import * as React from 'react'

import {
  Box,
  type EnhancedDimension,
  Icon,
  type LabelTextProps,
  LabeledInput,
  type LabeledInputProps,
  PopoverMenu,
} from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'

import Container from 'modules/competencies/components/CompetenciesFinder/Combobox/BaseCombobox/Container'
import type { PrivateProps as OptionProps } from 'modules/competencies/components/CompetenciesFinder/Combobox/BaseCombobox/Option'
import TagsInput from './TagsInput'

type ContainerProps = React.ComponentProps<typeof Container>
type PopoverMenuProps = React.ComponentProps<typeof PopoverMenu>

export type InheritedProps = Omit<ContainerProps, 'children' | 'onDebouncedSearch'> &
  Pick<LabeledInputProps, 'warning' | 'completed' | 'changed' | 'loading'> &
  Pick<LabelTextProps, 'secondaryLabel'>

export interface PopoverProps extends InheritedProps {
  placeholder: string
  label?: string
  description?: string
  footer?: React.ReactNode
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  maxHeight?: EnhancedDimension
  mandatory?: boolean
  isDisabled?: boolean
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void
  error?: string
  hideError?: boolean
  type?: PopoverMenuProps['type']
  'aria-label'?: string
  missingLabel: string
  selectValue: string
  defaultSearchPlaceholder: string
  children: React.ReactElement<OptionProps> | React.ReactElement<OptionProps>[]
  onDebouncedSearch?: (value: string) => void
}

const Popover: React.FC<PopoverProps> = ({
  children,
  mandatory,
  value,
  label,
  'aria-label': ariaLabel,
  secondaryLabel,
  description,
  footer,
  placeholder,
  icon,
  maxHeight = 'full',
  isDisabled,
  onToggle,
  isMultiSelect,
  warning,
  completed,
  changed,
  loading,
  onChange,
  isOpen = false,
  error,
  hideError,
  type = 'dialog',
  missingLabel,
  selectValue,
  defaultSearchPlaceholder,
  onDebouncedSearch,
  ...containerProps
}) => {
  const [open, setOpen] = React.useState(isOpen)
  useEffect(() => {
    if (isDisabled) return

    setOpen(isOpen)
  }, [isOpen, isDisabled])

  const options = Array.isArray(children) ? children : [children]

  const handleToggle = (v: boolean) => {
    if (onToggle) onToggle(v)
    setOpen(v)
  }

  const handleChange = (v: string[] | string | null) => {
    onChange(v)
    if (!isMultiSelect) {
      handleToggle(false)
    }
  }

  return (
    <Box width="full">
      <PopoverMenu type={type} onClick={handleToggle} open={open}>
        <PopoverMenu.Trigger disabled={isDisabled} aria-label={ariaLabel}>
          <LabeledInput
            label={label}
            aria-label={ariaLabel}
            secondaryLabel={secondaryLabel}
            description={description}
            mandatory={mandatory}
            value={value.length ? 'value' : ''}
            maxHeight={maxHeight}
            warning={warning}
            readonly={isDisabled}
            completed={completed}
            changed={changed}
            loading={loading}
            error={error}
            hideError={hideError}
            missingLabel={missingLabel}
            selectValue={selectValue}
          >
            <TagsInput
              aria-label={ariaLabel}
              placeholder={placeholder}
              value={value}
              options={options}
              onChange={onChange}
              icon={icon}
              disabled={isDisabled}
            />
          </LabeledInput>
        </PopoverMenu.Trigger>

        <PopoverMenu.Content side="bottom" align="center" width="s400">
          <Box paddingY="s16" paddingX="s32">
            <Container
              value={value}
              isMultiSelect={isMultiSelect}
              onChange={handleChange}
              defaultSearchPlaceholder={defaultSearchPlaceholder}
              onDebouncedSearch={onDebouncedSearch}
              {...containerProps}
            >
              {options}
            </Container>
            {footer && <Box paddingTop="s24">{footer}</Box>}
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}

export default Popover
